import { format } from 'date-fns';

/** `90` → `1ש 30ד`. */
export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h)}ש ${String(m).padStart(2, '0')}ד`;
}

export interface ManualEntryValues {
  date: string; // yyyy-MM-dd, local
  start_time: string; // HH:mm, local
  hours: number;
  minutes: number;
}

/**
 * A manual entry is typed as "when it started + how long", because that is how
 * people remember work. The table stores a start and an end; duration_minutes
 * is generated from them.
 */
export function rangeFromManual(values: ManualEntryValues): {
  started_at: string;
  ended_at: string;
} {
  const start = new Date(`${values.date}T${values.start_time}:00`);
  const totalMinutes = Math.round(values.hours * 60 + values.minutes);
  const end = new Date(start.getTime() + totalMinutes * 60_000);
  return { started_at: start.toISOString(), ended_at: end.toISOString() };
}

export function manualFromRange(startedAt: string, endedAt: string | null): ManualEntryValues {
  const start = new Date(startedAt);
  const totalMinutes = endedAt
    ? Math.max(0, Math.round((new Date(endedAt).getTime() - start.getTime()) / 60_000))
    : 0;
  return {
    date: format(start, 'yyyy-MM-dd'),
    start_time: format(start, 'HH:mm'),
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  };
}

export interface HoursSummaryInput {
  duration_minutes: number | null;
  billable: boolean;
  hourly_rate: number | null;
}

export interface HoursSummary {
  totalMinutes: number;
  billableMinutes: number;
  /** Billable time × rate. The entry's own rate wins; the project rate fills in. */
  billableValue: number;
  /** Billable minutes that had no rate anywhere, so are not in billableValue. */
  unpricedMinutes: number;
}

export function summarizeHours(
  entries: HoursSummaryInput[],
  projectRate: number | null,
): HoursSummary {
  let totalMinutes = 0;
  let billableMinutes = 0;
  let billableValue = 0;
  let unpricedMinutes = 0;
  for (const e of entries) {
    // A running timer has no duration yet and is not counted.
    if (e.duration_minutes === null) continue;
    totalMinutes += e.duration_minutes;
    if (!e.billable) continue;
    billableMinutes += e.duration_minutes;
    const rate = e.hourly_rate ?? projectRate;
    if (rate === null) unpricedMinutes += e.duration_minutes;
    else billableValue += (e.duration_minutes / 60) * rate;
  }
  return {
    totalMinutes,
    billableMinutes,
    billableValue: Math.round(billableValue * 100) / 100,
    unpricedMinutes,
  };
}
